import React from 'react'
import { useRef, useMemo, useCallback, useEffect, useReducerState, useUniqueId } from 'sb-hooks'

import MenuContext from './MenuContext'
import type { Refs, State } from './MenuContext'


type MenuProps = {
  children: any
  toggleOnHover?: boolean
  closeOnBlur?: boolean
  fullWidth?: boolean
}

const Menu: React.FunctionComponent<MenuProps> = (props) => {
  const { children, toggleOnHover, closeOnBlur, fullWidth } = props

  const refs = useRef<Refs>({
    button: null,
    list: null,
    items: [],
  })

  const buttonId = useUniqueId()

  const initialState = useMemo(() => ({
    buttonId,
    buttonRect: null, // this value set in MenuButton component
    selectionIndex: -1,
    isOpen: false,
    isKeyDown: false,
    closingWithClick: false,
  }), []) // eslint-disable-line react-hooks/exhaustive-deps

  const mounted = useRef(false)

  const prevValues = useRef<{ isOpen: boolean, selectionIndex: number }>({
    isOpen: initialState.isOpen,
    selectionIndex: initialState.selectionIndex,
  })

  const [ state, setState ] = useReducerState<State>(initialState)
  const { buttonRect, selectionIndex, isOpen, isKeyDown, closingWithClick } = state

  useEffect(() => {
    // ATTN it should only call on deps update
    if (!mounted.current) {
      mounted.current = true
      return
    }

    if (isOpen && !prevValues.current.isOpen) {
      if (selectionIndex !== -1) {
        // haven't measured the popover yet, give it a frame otherwise
        // we'll scroll to the bottom of the page >.<
        requestAnimationFrame(() => {
          if (refs.current.items[selectionIndex] && isKeyDown) {
            refs.current.items[selectionIndex].focus()
          }
        })
      }
      else {
        refs.current.list.focus()
      }
    }
    else if (!isOpen && prevValues.current.isOpen) {
      if (isKeyDown) {
        refs.current.button.focus()
      }
    }
    else if (selectionIndex !== prevValues.current.selectionIndex) {
      if (selectionIndex === -1) {
        // when mousing over non-menu list items focus on the menu list
        // so the the keyboard will work after a mouseover
        refs.current.list.focus()
      }
      else {
        if (refs.current.items[selectionIndex]) {
          refs.current.items[selectionIndex].focus()
        }
      }
    }

    prevValues.current = { isOpen, selectionIndex }
  }, [ isOpen, selectionIndex ]) // eslint-disable-line react-hooks/exhaustive-deps

  const open = useCallback(({ isKeyDown = false } = {}) => {
    setState({
      isOpen: true,
      isKeyDown,
      selectionIndex: 0,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(({ isKeyDown = false } = {}) => {
    setState({
      isOpen: false,
      isKeyDown,
      selectionIndex: -1,
      closingWithClick: false,
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMenuClick = useCallback(() => {
    if (isOpen) {
      close()
    }
    else {
      open()
    }
  }, [ open, close, isOpen ])

  const handleMenuKeyDown = useCallback((event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
      event.preventDefault()

      open({ isKeyDown: true })
    }
  }, [ open ])

  const handleMenuMouseDown = useCallback(() => {
    if (isOpen) {
      setState({
        closingWithClick: true,
      })
    }
  }, [ isOpen ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleMenuMouseEnter = useCallback(() => {
    if (toggleOnHover) {
      open()
    }
  }, [ open, toggleOnHover ])

  const handleMenuMouseLeave = useCallback(() => {
    if (toggleOnHover) {
      setTimeout(() => {
        if (refs.current.list && !refs.current.list.getAttribute('data-menulist-hovered')) {
          close()
        }
      })
    }
  }, [ close, toggleOnHover ])

  const handleMenuBlur = useCallback(() => {
    if (closeOnBlur) {
      close()
    }
  }, [ close, closeOnBlur ])

  const handleListBlur = useCallback((event) => {
    if (!closingWithClick && !refs.current.list.contains(event.relatedTarget)) {
      close()
    }
  }, [ close, closingWithClick ])

  const handleListKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      close({ isKeyDown: true })
    }
    else if (event.key === 'ArrowDown') {
      event.preventDefault() // prevent window scroll

      const nextIndex = selectionIndex + 1

      if (nextIndex !== refs.current.items.length) {
        setState({ selectionIndex: nextIndex })
      }
    }
    else if (event.key === 'ArrowUp') {
      event.preventDefault() // prevent window scroll

      const nextIndex = selectionIndex - 1

      if (nextIndex !== -1) {
        setState({
          selectionIndex: nextIndex,
        })
      }
    }
    else if (event.key === 'Tab') {
      refs.current.button.focus()
    }
  }, [ close, selectionIndex ]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleListMouseEnter = useCallback(() => {
    if (refs.current.list) {
      refs.current.list.setAttribute('data-menulist-hovered', 'true')
    }
  }, [])

  const handleListMouseLeave = useCallback(() => {
    if (toggleOnHover) {
      close()
      refs.current.list.removeAttribute('data-menulist-hovered')
    }
  }, [ close, toggleOnHover ])

  const getListStyles = useCallback((listRect) => {
    if (!buttonRect) {
      return {
        opacity: 0,
      }
    }

    const styles: any = {
      position: 'absolute',
      zIndex: 200,
      left: `${buttonRect.left}px`,
      top: `${buttonRect.top + buttonRect.height}px`,
    }

    if (!listRect) {
      return {
        ...styles,
        opacity: 0,
      }
    }

    if (buttonRect.width < 500) {
      styles.minWidth = buttonRect.width
    }

    const collisions = {
      top: buttonRect.top - listRect.height < 0,
      right: window.innerWidth < buttonRect.left + listRect.width,
      bottom: window.innerHeight < buttonRect.top + listRect.height,
      left: buttonRect.left - listRect.width < 0,
    }

    const directionRight = collisions.right && !collisions.left
    const directionUp = collisions.bottom && !collisions.top

    if (fullWidth) {
      return {
        ...styles,
        left: '50%',
        top: directionUp
          ? `${buttonRect.top - listRect.height + window.scrollY}px`
          : `${buttonRect.top + buttonRect.height + window.scrollY}px`,
        width: '96%',
        maxWidth: '1240px',
        transform: 'translate3d(-50%, 0, 0)',
      }
    }

    return {
      ...styles,
      left: directionRight
        ? `${buttonRect.right - listRect.width + window.scrollX}px`
        : `${buttonRect.left + window.scrollX}px`,
      top: directionUp
        ? `${buttonRect.top - listRect.height + window.scrollY}px`
        : `${buttonRect.top + buttonRect.height + window.scrollY}px`,
    }
  }, [ buttonRect, fullWidth ])

  const providerValue = useMemo(() => ({
    refs,
    state,
    setState,
    handleMenuBlur,
    handleMenuClick,
    handleMenuKeyDown,
    handleMenuMouseDown,
    handleMenuMouseEnter,
    handleMenuMouseLeave,
    handleListBlur,
    handleListKeyDown,
    handleListMouseEnter,
    handleListMouseLeave,
    getListStyles,
  }), [
    refs,
    state,
    setState,
    handleMenuBlur,
    handleMenuClick,
    handleMenuKeyDown,
    handleMenuMouseDown,
    handleMenuMouseEnter,
    handleMenuMouseLeave,
    handleListBlur,
    handleListKeyDown,
    handleListMouseEnter,
    handleListMouseLeave,
    getListStyles,
  ])

  return (
    <MenuContext.Provider value={providerValue}>
      {
        typeof children === 'function'
          ? children({ isOpen })
          : children
      }
    </MenuContext.Provider>
  )
}


export default React.memo(Menu)
